/**
 * useTreasureHunt.js
 *
 * Drop-in hook for any component that is a "treasure" location.
 */

import { useCallback } from "react"
import { useSelector } from "react-redux"
import { selectAuth } from "shared/redux/slices/authSlice"
import { isRole, Role } from "constant/Role"
import { useTreasureContext } from "components/TreasurePopup/TreasurePopup"

const STORAGE_KEY_PREFIX = "treasure_hunt_collected_"

// ── localStorage helpers ───────────────────────────────────────────────────
function getCollected(userId) {
  try {
    const raw = localStorage.getItem(STORAGE_KEY_PREFIX + userId)
    return raw ? JSON.parse(raw) : []
  } catch {
    return []
  }
}

function markCollected(userId, componentId) {
  try {
    const current = getCollected(userId)
    if (!current.includes(componentId)) {
      localStorage.setItem(
        STORAGE_KEY_PREFIX + userId,
        JSON.stringify([...current, componentId])
      )
    }
  } catch {
    // localStorage blocked (private mode etc.) — fail silently
  }
}

export function isAlreadyCollected(userId, componentId) {
  return getCollected(userId).includes(componentId)
}

export function resetTreasureHunt(userId) {
  try {
    localStorage.removeItem(STORAGE_KEY_PREFIX + userId)
  } catch {
    // ignore
  }
}

// ── Hook ───────────────────────────────────────────────────────────────────
export function useTreasureHunt(input) {
  const ctx = useTreasureContext()
  const auth = useSelector(selectAuth)
  const isLxo = isRole(auth?.role, Role.LXO)

  const normalized =
    typeof input === "string"
      ? { componentId: input }
      : input || {}

  const {
    n8nUrl = ctx?.n8nUrl,
    userId = ctx?.userId,
    componentId,
    onShow = ctx?.openPopup,
  } = normalized

  const triggerIfNew = useCallback(() => {
    // Treasure hunt disabled — no-op for all users.
    return
  }, [n8nUrl, userId, componentId, onShow, isLxo])

  return { triggerIfNew }
}
